import type { AutoTagJobState } from '@prisma/client';

/**
 * Tunables and value types for auto-tagging (PRD 05).
 *
 * These deliberately live outside `providers/`: `MediaModule` registers
 * `Object.values(providers)` as its DI provider list, so that barrel must
 * contain service classes and nothing else — a plain constant exported from it
 * is handed to Nest as a provider and fails at boot.
 */

/**
 * Bump when the render changes in a way that alters output. Folded into
 * `specHash`, so a bump invalidates every render at once and the `Ready`
 * sweeper re-queues them without a hand-written backfill.
 */
export const RENDER_SPEC_VERSION = 1;

/**
 * Explicit code-level defaults, layered under the shop composite and then the
 * per-track composite.
 *
 * These are NOT Prisma defaults on purpose: a Prisma composite default only
 * materialises when the composite itself is written, and both
 * `ShopSettings.autoTag` and `TrackPreview.autoTag` are nullable. A merchant
 * who uploads a voice tag without ever opening the settings form has no
 * composite at all, and every field would otherwise resolve to `undefined`.
 */
export const RENDER_DEFAULTS = {
  voiceTagFileId: null as string | null,
  /** 0 renders the whole track; a non-zero value clips to N seconds. */
  previewSeconds: 0,
  intervalSeconds: 20,
  gain: 'medium',
  character: 'normal',
};

/**
 * 15–30s is the industry band (Airbit documents 20–30s; dedicated tagging tools
 * ship 15/30/45/60 presets). Below 10s the preview becomes unlistenable and
 * buyers skip it; above 30s a single intro tag is trivially edited out.
 */
export const MIN_INTERVAL_SECONDS = 10;
export const MAX_INTERVAL_SECONDS = 30;

/** Offset of the tag against the master's programme loudness, in LU. */
export const GAINS = ['light', 'medium', 'heavy'] as const;
export type Gain = (typeof GAINS)[number];

/**
 * Voice character, applied to the tag inside the render rather than baked into
 * the stored audio. Keeping it here means a merchant can flip it without
 * re-generating (no ElevenLabs credits burnt), it works for tags they RECORDED
 * as well as ones we generated, and the existing spec-hash machinery re-renders
 * the catalogue automatically when it changes.
 *
 * `deep` is a true pitch shift: ElevenLabs' own `speed` setting is
 * pitch-preserving time-stretch (measured — spectral centroid holds at ~4.2 kHz
 * across a 1.9x duration change), so it cannot produce this.
 */
export const CHARACTERS = ['normal', 'deep'] as const;
export type Character = (typeof CHARACTERS)[number];

/** ~2.8 semitones down, measured: 195 Hz -> 166 Hz centroid, duration unchanged. */
export const DEEP_PITCH_FACTOR = 0.85;

export interface RenderSpec {
  voiceTagFileId: string | null;
  previewSeconds: number;
  intervalSeconds: number;
  gain: string;
  character: string;
}

/**
 * Shape of `ShopSettings.autoTag` / `TrackAutoTag`. Declared here rather than
 * imported because Prisma inlines MongoDB composite types into each model
 * instead of exporting them by name; these are structurally compatible with
 * both, so callers pass the Prisma value straight in with no assertion.
 */
export interface AutoTagSettingsInput {
  previewSeconds?: number | null;
  intervalSeconds?: number | null;
  gain?: string | null;
  character?: string | null;
}

export interface TrackAutoTagInput extends AutoTagSettingsInput {
  enabled?: boolean | null;
  voiceTagFileId?: string | null;
}

/**
 * States a job can be re-queued *from*. `Skipped` is deliberately absent: it is
 * only ever assigned at creation or when a manual `Tag` upload becomes usable,
 * and moving a live row into it would un-latch the track (PRD 05 §8).
 */
export const RESUMABLE_STATES: AutoTagJobState[] = [
  'Queued',
  'Rendering',
  'Ready',
  'Failed',
  'Blocked',
];

/** Thrown when a render failed in a way that is worth another attempt. */
export class TransientRenderError extends Error {}

/** The rendered artefact, as returned by TagRenderFn and stored on the job. */
export interface RenderedAsset {
  key: string;
  name: string;
  contentType: string;
  bytes: number;
  secure_url: string;
}
